import React, { KeyboardEvent, useRef, useState, useEffect, useMemo } from 'react'

import { systemFieldsSelector } from 'features/assets/model/selectors/assetsSelectors'
import { IFieldOption } from 'features/assets/model/types/assets'
import {
  selectSearchAiTagsSelector,
  selectSearchIsTagMenuVisibleSelector,
  selectSearchStatusSelector,
  selectSearchTagsTypeSelector
} from 'features/common/model/selectors/searchSelectors'
import {
  clearAllTags,
  clearSearchResults,
  clearSearchState,
  setIsSearchInputActive,
  setIsSearchPage,
  setIsTagMenuVisible,
  setPrevStateSearch
} from 'features/common/model/slice/searchSlice'
import { SearchTagsType } from 'features/common/model/types/search'
import { getStatusColor } from 'features/common/utils/statusColors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { useOnClickOutside } from 'shared/hooks/useOnclickOutside'
import { CloseOutlined, Divider, SearchOutlined, Tag, TagOutlined } from 'shared/ui'
import styled from 'styled-components'
import { clearSearchParamsFromURL } from 'shared/utils/urlUtils'

interface TagInputProps {
  currentTags?: IFieldOption[]
  placeholder?: string;
  className?: string
  isEditable?: boolean
  isSearchInput?: boolean
  needComma?: boolean
  onFocusSearchInput?: () => void
  onChange?: (tag: IFieldOption) => void
  onRemoveTag?: (tag: string) => void
}

export const TagInput: React.FC<TagInputProps> = ({
  currentTags,
  placeholder,
  className,
  isEditable = true,
  needComma = false,
  isSearchInput = false,
  onFocusSearchInput,
  onChange,
  onRemoveTag
}) => {
  const [inputValue, setInputValue] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const dispatch = useAppDispatch()
  const searchAiTags = useAppSelector(selectSearchAiTagsSelector)
  const searchStatus = useAppSelector(selectSearchStatusSelector)
  const searchTagsType = useAppSelector(selectSearchTagsTypeSelector)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const allTags = useAppSelector(systemFieldsSelector)?.find(field => field.title === 'tags')?.options
  const isTagMenuVisible = useAppSelector(selectSearchIsTagMenuVisibleSelector)
  // get available tags
  const availableTags = useMemo(() => {
    if (!allTags) return []
    return allTags.filter((tag) => 
      !currentTags?.some(currentTag => currentTag.id === tag.id)
    )
  }, [allTags, currentTags])

  // filter tags by input value
  const filteredTags = useMemo(() => {
    if (!inputValue.trim()) return availableTags
    
    const searchTerm = inputValue.toLowerCase()
    return availableTags.filter(tag =>
      tag.value?.toLowerCase().includes(searchTerm)
    )
  }, [availableTags, inputValue])


  // handle click outside component to hide dropdown
  useOnClickOutside(containerRef, () => setShowDropdown(false))

  // handle escape key to hide dropdown
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false)
        if (inputRef.current) {
          inputRef.current.blur()
        }
      }
    }

    if (showDropdown) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showDropdown])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
  }

  const handleSelectTag = (tag: IFieldOption, e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    const isTagAlreadySelected = currentTags?.some(selectedTag => selectedTag.id === tag.id)

    if (!isTagAlreadySelected) {
      onChange?.(tag)
    }

    setInputValue('')
    setShowDropdown(false)
  }

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault()
      setInputValue('')
      setShowDropdown(false)

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 0)
    }
  }

  const handleInputFocus = () => {
    setShowDropdown(true)
  }

  const handleRemoveTag = (removedTag: string) => {
    onRemoveTag?.(removedTag)
  }

  const getSearchTypeText = (searchType: SearchTagsType) => {
    switch (searchType) {
    case SearchTagsType.CONTAIN:
      return 'in'
    case SearchTagsType.CONTAIN_ONE_OF:
      return 'is'
    case SearchTagsType.DOESNT_CONTAIN:
      return 'not in'
    default:
      return 'in'
    }
  }

  const handleOpenMenuTags = () => {
    if(currentTags.length > 0 ) {
      dispatch(setIsTagMenuVisible(true))
      dispatch(setIsSearchInputActive(true))
    } else if((searchAiTags.length > 0 || searchStatus.length > 0) && !isTagMenuVisible) {
      dispatch(setPrevStateSearch())
      dispatch(setIsTagMenuVisible(false))
      dispatch(setIsSearchInputActive(true))
      onFocusSearchInput()
    }
  }

  const handleCloseMenuTags = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    dispatch(setIsTagMenuVisible(false))
    dispatch(setIsSearchInputActive(false))
    dispatch(clearAllTags())
    dispatch(setIsSearchPage(false))
    dispatch(clearSearchResults())
    clearSearchParamsFromURL()
    dispatch(clearSearchState())
  }

  const searchTypeText = getSearchTypeText(searchTagsType)

  const getTagsByType = () => {
    if(currentTags.length > 0) {
      return currentTags.map((tag,index) => (
        <>
          <STagInInput
            marginRight={isSearchInput ? '0' : '4px'}
            key={tag.id}
            closable
            onClose={() => handleRemoveTag(tag.id)}
          >
            {tag.value}
          </STagInInput>

          {index < currentTags.length - 1 && needComma && ','}
        </>
      ))
    }

    if(searchAiTags.length > 0) {
      return searchAiTags.map(tag => (
        <SAiTag
          marginRight={isSearchInput ? '0' : '4px'}
          key={tag.name}
          icon={<SAiIcon src={'/icons/ai.svg'} />}
        >
          {tag.name}
        </SAiTag>
      ))
    }

    if(searchStatus.length > 0) {
      return searchStatus.map(tag => (
        <SStatus
          marginRight={isSearchInput ? '0' : '4px'}
          key={tag.id}
          color={getStatusColor(tag.value)}
        >
          {tag.value}
        </SStatus>
      ))
    }
  }

  return (
    <STagInputContainer
      ref={containerRef}
      className={className}
      onClick={handleOpenMenuTags}
    >
      {
        !isEditable && (
          <>
            <SSearchOutlined  />

            {`Tags ${searchTypeText + ' '}`}
          </>
        )
      }

      {getTagsByType()}

      {isEditable && (
        <>
          <STagInput
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            placeholder={currentTags.length === 0 ? placeholder : ''}
          />

          {showDropdown && (
            <STagsDropdown>
              {filteredTags.length > 0 ? (
                filteredTags.map(tag => (
                  <STagOption
                    key={tag.id}
                    onClick={(e) => handleSelectTag(tag, e)}
                  >
                    {tag.value || tag.name}
                  </STagOption>
                ))
              ) : (
                <SNoOptions>
No options
                </SNoOptions>
              )}
            </STagsDropdown>
          )}
        </>
      )}

      {!isEditable && (
        <SIconsContainer>
          <SCloseOutlined onClick={handleCloseMenuTags} />

          <SDivider type="vertical" />

          <STagOutlined onClick={handleOpenMenuTags} />
        </SIconsContainer>
      )}

    </STagInputContainer>
  )
}

const SAiIcon = styled.img`
  width: 12px;
  height: 12px;
`

const SAiTag = styled(Tag)<{ marginRight: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 0;
  cursor: pointer;
  border: 0;
  background: rgba(0, 0, 0, 0.06);

  margin-right: ${({ marginRight }) => marginRight};
`

const SStatus = styled(Tag)<{ marginRight: string }>`
  display: flex;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;

  margin-right: ${({ marginRight }) => marginRight};
`

const SIconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
`

const STagOutlined = styled(TagOutlined)`
  color: #00000073;
  cursor: pointer;
`

const SDivider = styled(Divider)`
  color: #00000073;
`

const SCloseOutlined = styled(CloseOutlined)`
  color: #00000073;
  cursor: pointer;
`

const SSearchOutlined = styled(SearchOutlined)`
  color: #00000073;
`

const STagInInput = styled(Tag)<{ marginRight: string }>`
  height: 100%;
  color: rgba(0, 0, 0, 0.88);
  font-family: "SF Pro Text";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  border: 0;
  background: rgba(0, 0, 0, 0.06);
  margin-right: ${({ marginRight }) => marginRight};
`

const STagInputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 0 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-height: 32px;
  cursor: text;
  position: relative;

  &.search-input-field-tag-input {
    margin-left: 10px;
    gap: 4px;
  }

  &:hover, &:focus-within {
    border-color: var(--color-primary);
  }
`

const STagInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  min-width: 80px;
  padding: 0;
  background: transparent;
`

const STagsDropdown = styled.div`
  position: absolute;
  left: 0;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 4px;
  width: 100%;
  top: 35px;
  max-height: 200px;
`

const STagOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`

const SNoOptions = styled.div`
  padding: 8px 12px;
  color: rgba(0, 0, 0, 0.45);
`
